import { Cartesian3, Cartographic, Color, createGuid } from "@cesium/engine";
import Utils from "../basic/Utils";
import DitapViewer from "../basic/DitapViewer";

/**
 * 고도 계산 및 영역 표시 클래스.</br>
 * Altitude calculation and area display class.
 *
 * @alias MeasurementAltitude
 * @constructor
 *
 * @param {object} options 아래 속성 포함 객체.</br>Object with the following properties:
 * @param {DitapViewer} options.viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {Cartesian3} [options.position] 고도 측정을 위한 Cartesian 좌표. 입력한 경우 입력 값으로 고도 계산 및 영역 표시되어 수정 불가.</br>
 * Cartesian value to measure elevation. If provided, the input value is used for altitude calculation and region display, and it cannot be modified.
 * @param {Color} [options.entityColor = Color.fromBytes(252, 76, 2, 255)] 엔티티 색상.<br> Color of the entity.
 *
 * @example
 * // 샌드캐슬 예제 'DiTAP - Altitude Measurement' 참조
 *
 *  const viewer = new DitapViewer("ditapContainer");
 *
 * // 1. 지정된 영역 생성. (InteractionAltitude를 통한 수정 불가.)
 * const position = new Ditap.Cartesian3(-3044793.4521523346, 4043760.1510156766, 3867436.511895938);
 *
 * const options = {
 *  viewer: viewer,
 *  position: position,
 *  entityColor: Color.fromBytes(252, 76, 2, 255)
 * };
 *
 * const measurementAltitude = new Ditap.MeasurementAltitude(options);
 *
 * // 2. 마우스로 영역 지정 후 생성. (InteractionAltitude를 통해 고도 측정 및 영역 표시.)
 * const measurementAltitude = new Ditap.MeasurementAltitude({
 *  viewer: viewer
 * });
 *
 * const interactionAltitude = new Ditap.InteractionAltitude();
 * interactionAltitude.measurementAltitude = measurementAltitude;
 */
class MeasurementAltitude {
  constructor(options) {
    const viewer = options.viewer;
    const position = options.position;
    const entityColor = options.entityColor;

    this._id = `MeasurementAltitude${createGuid()}`;
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
      viewer.ditapStorage.measurementAltitude[this.id] = this;
    } else {
      console.error("Type doesn't match");
    }
    if (entityColor instanceof Color) {
      this._entityColor = entityColor;
    } else {
      this._entityColor = Color.fromBytes(252, 76, 2, 255);
    }

    this._pointEntity = undefined;
    this._label = undefined;
    this._labelHeight = undefined;
    if (position instanceof Cartesian3) {
      this._position = position;
      this.createAltitudeEntity();
    } else {
      this._position = undefined;
    }
    this._altitude = undefined;
    this._displayCondition = 2000;
    this._cameraChangedEvent = undefined;
    this._windowResizeEvent = undefined;
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  set viewer(viewer) {
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
    }
  }
  get viewer() {
    return this._viewer;
  }

  /*
   * 측정 객체 ID. </br>
   * ID to assign to the measurement object.
   * @type {string}
   */
  set id(id) {
    this._id = id;
  }
  get id() {
    return this._id;
  }

  /**
   * 고도 측정 후 생성된 Point Entity.</br>
   * Point entity created after altitude measurement.
   * @type {Entity}
   * @readonly
   */
  set pointEntity(pointEntity) {
    this._pointEntity = pointEntity;
  }
  get pointEntity() {
    return this._pointEntity;
  }

  /*
   * 고도 측정 후 생성된 라벨 요소. </br>
   * Label element created after altitude measurement.
   * @type {Element}
   */
  set label(label) {
    this._label = label;
  }
  get label() {
    return this._label;
  }

  /*
   * 라벨 내부 높이. </br>
   * Internal height of the label element.
   * @type {number}
   */
  set labelHeight(labelHeight) {
    this._labelHeight = labelHeight;
  }
  get labelHeight() {
    return this._labelHeight;
  }

  /**
   * 고도 측정 할 Cartesian 좌표. </br>
   * Altitude measurement cartesian.
   * @type {Cartesian3}
   * @readonly
   */
  set position(position) {
    this._position = position;
  }
  get position() {
    return this._position;
  }

  /**
   * 측정된 고도 값. </br>
   * Measured altitude value.
   * @type {number}
   * @readonly
   */
  set altitude(altitude) {
    this._altitude = altitude;
  }
  get altitude() {
    return this._altitude;
  }

  /*
   * 카메라 시점 변경 시 실행 이벤트. </br>
   * Events to run when changing camera viewpoint in window.
   * @type {Function}
   */
  set cameraChangedEvent(cameraChangedEvent) {
    this._cameraChangedEvent = cameraChangedEvent;
  }
  get cameraChangedEvent() {
    return this._cameraChangedEvent;
  }

  /*
   * 윈도우 크기 조절 시 실행 이벤트.</br>
   * Events to run when window resizing.
   * @type {Function}
   */
  set windowResizeEvent(windowResizeEvent) {
    this._windowResizeEvent = windowResizeEvent;
  }
  get windowResizeEvent() {
    return this._windowResizeEvent;
  }

  /*
   * 엔티티의 DistanceDisplayCondition 값.</br>
   * DistanceDisplayCondition value of entity.
   * @type {number}
   */
  set displayCondition(displayCondition) {
    this._displayCondition = displayCondition;
  }
  get displayCondition() {
    return this._displayCondition;
  }

  /**
   * 포인트 엔티티 색상.<br>
   * Color of the point entity.
   */
  set entityColor(entityColor) {
    if (entityColor instanceof Color) {
      this._entityColor = entityColor;
      Utils.changePolylineColor(this.pointEntity, entityColor);
    }
  }
  get entityColor() {
    return this._entityColor;
  }

  /**
   * 인스턴스 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the instance. Should be called if permanently removing the instance from layout.
   */
  destroy() {
    // entity 제거
    this.viewer.entities.remove(this.pointEntity);

    if (this.label) {
      this.label.remove();
    }

    if (this.label instanceof Element) {
      this.label.removeEventListener("mouseover", Utils.hideLabelElement);
      this.label.removeEventListener("mouseover", Utils.showLabelElement);
    }

    // viewerStorage에서 삭제
    this.viewer.ditapStorage.removeInstanceById(this.id);

    this.removeChangeEvent();

    // 변수 초기화
    this.id = undefined;
    this.pointEntity = undefined;
    this.label = undefined;
    this.labelHeight = undefined;
    this.position = undefined;
    this.altitude = undefined;
    this.cameraChangedEvent = undefined;
    this.windowResizeEvent = undefined;
    this.resizeObserver = undefined;
  }

  /*
   * Cartesian 좌표로 고도 계산. </br>
   * Calculate altitude with cartesian.
   * @param {Cartesian3} cartesian 고도 계산 할 Cartesian 좌표. </br> Cartesian3 to measure altitude
   * @returns {number} 계산된 고도 값. </br> calculated altitude.
   */
  calculateAltitude(position) {
    const altitude = Number(
      Cartographic.fromCartesian(position).height.toFixed(2)
    );
    return altitude;
  }

  /*
   * 측정된 고도 Entity 생성. </br>
   * Create measured altitude entities.
   */
  createAltitudeEntity() {
    const displayCondition = Utils.getDistanceDisplayConditon([this.position]);

    if (displayCondition > 2000) {
      this.displayCondition = displayCondition;
    }
    const entity = Utils.createPoint({
      viewer: this.viewer,
      id: this.id,
      position: this.position,
      outlineColor: this.entityColor,
      displayCondition: this.displayCondition,
    });
    this.pointEntity = entity;

    const altitude = this.calculateAltitude(this.position);
    this.altitude = altitude;

    const label = Utils.createTotalLabel(this.viewer, altitude);
    this.label = label;
    this.labelHeight = label.clientHeight;

    Utils.changeLabelLocation(
      this.viewer,
      this.position,
      label,
      label.clientHeight
    );

    if (this.cameraChangedEvent === undefined) {
      this.createCameraMoveEvent();
    }
    if (this.windowResizeEvent === undefined) {
      this.createWindowResizeEvent();
    }
  }

  /*
   * 윈도우 크기 조절 이벤트 제거.</br>
   * Create the window resize event.
   */
  removeChangeEvent() {
    if (this.cameraChangedEvent) {
      this.cameraChangedEvent();
    }

    if (this.windowResizeEvent) {
      window.removeEventListener("resize", this.windowResizeEvent);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   */
  createWindowResizeEvent() {
    const viewer = this.viewer;
    const container = this.viewer.container;
    const labelEntity = this.label;
    const positions = this.position;
    const height = this.labelHeight;
    const displayCondition = this.displayCondition;

    const resizeOriginal = () => {
      Utils.changeTotalLabelVisibility(
        viewer,
        labelEntity,
        positions,
        displayCondition,
        height
      );
    };

    this.windowResizeEvent = () => {
      resizeOriginal();

      setTimeout(resizeOriginal, 500);
    };
    window.addEventListener("resize", this.windowResizeEvent);

    // ResizeObserver 객체를 생성하고 콜백 함수 전달.
    this.resizeObserver = new ResizeObserver(resizeOriginal);

    // 대상 요소에 대한 크기 변화 관찰.
    this.resizeObserver.observe(container);
  }

  /*
   * 카메라 시점 이동 시 이벤트 생성. </br>
   * Create an event when moving the camera point of view.
   */
  createCameraMoveEvent() {
    const viewer = this.viewer;
    const labelEntity = this.label;
    const positions = this.position;
    const height = this.labelHeight;
    const displayCondition = this.displayCondition;

    // 카메라 이동 이벤트 생성
    this.cameraChangedEvent = viewer.camera.changed.addEventListener(() => {
      Utils.changeTotalLabelVisibility(
        viewer,
        labelEntity,
        positions,
        displayCondition,
        height
      );
    });
  }
}
export default MeasurementAltitude;
